import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { View, ActivityIndicator } from 'react-native-web';

import { shallow } from 'zustand/shallow';
import PageButton from './PageButton';
import getPagesToRender from './helpers/getPagesToRender';
import chevronLeft from '../../../../images/icons/chevron-back-outline.svg';
import chevronRight from '../../../../images/icons/chevron-forward-outline.svg';
import useDiscoverStore from '../../../../zustand-stores/DiscoverStore';

import styles from './styles';

/**
 * TODO: move to shared library
 * Page number nav for discover
 *
 * @param {Object} props
 * @param {number} props.count - total number of discovered programs
 * @param {number} props.currentPage - current page
 * @param {function} props.fetchPageResults - fetches new page of results
 * @returns {JSX.Element} PageNumberNav component
 */

const propTypes = {
  count: PropTypes.number,
  fetchPageResults: PropTypes.func.isRequired,
};

const defaultProps = {
  count: 1,
};

const PageNumberNav = ({ count, fetchPageResults }) => {
  const { loading, numPerPage, currentPage, resultsCount } = useDiscoverStore(
    store => ({
      loading: store.queryStatus.loading,
      numPerPage: store.numPerPage,
      currentPage: store.currentPage,
      resultsCount: store.queryStatus.resultsCount,
    }),
    shallow
  );

  const pages = Math.ceil(count / numPerPage) || 1;

  const onPress = useCallback(
    page => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      if (typeof page === 'number') fetchPageResults(page);
    },
    [fetchPageResults]
  );

  const PageNumberNavMemo = useMemo(() => {
    const pagesArray = getPagesToRender({
      pages,
      currentPage,
    });
    return pagesArray.map(page => {
      return (
        <PageButton
          key={page} // `undefined` is a potential value but there should only be 1
          page={page}
          selected={page === currentPage}
          onPress={() => onPress(page)}
        />
      );
    });
  }, [pages, onPress, currentPage]);

  if (loading) {
    // resultsCount and loading are not reliable to prevent "no results"
    // from flashing on the screen for a second
    return (
      <View style={styles.container}>
        <ActivityIndicator />
      </View>
    );
  }

  if (!resultsCount) return null;

  return (
    <View style={styles.container}>
      <PageButton
        disabled={currentPage === 1}
        imageUrl={chevronLeft}
        onPress={() => {
          if (currentPage > 1) onPress(currentPage - 1);
        }}
      />
      {PageNumberNavMemo}
      <PageButton
        disabled={currentPage === pages}
        imageUrl={chevronRight}
        onPress={() => {
          if (currentPage < pages) onPress(currentPage + 1);
        }}
      />
    </View>
  );
};

PageNumberNav.propTypes = propTypes;
PageNumberNav.defaultProps = defaultProps;

export default PageNumberNav;
